import React, { useEffect, useMemo, useState } from 'react';
import { Search, FileCheck, FileClock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { AdminGeoMapCanvas } from './AdminGeoMapCanvas';
import { buildListingMapItem, buildVenueMapItem } from './AdminGeoMapExplorer';
import { AdminDetailSection, Badge, Card, adminDetailFieldCardStyle } from './AdminPanelPrimitives';
import { StatusChip, EmptyState } from './adminDesignSystem';
import { buildShareUrl } from '../../utils/shareBaseUrl';
import { ApprovalsDetailView } from './ApprovalsDetailView';

const copyInviteVenueText = async (text) => {
  if (!text) return false;
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // continue to fallback
    }
  }
  window.prompt('Copy this invite', text);
  return false;
};

const dedupeById = (items) => {
  const itemMap = new Map();
  (items || []).filter(Boolean).forEach((item) => {
    if (!itemMap.has(item.id)) {
      itemMap.set(item.id, item);
    }
  });
  return Array.from(itemMap.values());
};

const toggleMultiFilter = (selectedFilters, nextFilter) => {
  if (!Array.isArray(selectedFilters)) {
    return [nextFilter];
  }
  return selectedFilters.includes(nextFilter)
    ? selectedFilters.filter((entry) => entry !== nextFilter)
    : [...selectedFilters, nextFilter];
};

const buildActiveVenueMapItem = (venue) => {
  const latitude = Number(venue?.latitude);
  const longitude = Number(venue?.longitude);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return null;
  }

  return {
    id: `live-venue-${venue.id}`,
    entityType: 'venue',
    title: venue.name || venue.map_name || venue.restaurant_name || 'Active venue',
    subtitle: venue.address || [venue.city, venue.country].filter(Boolean).join(', ') || 'Approximate venue location',
    badgeText: 'Active venue',
    badgeColor: 'blue',
    markerColor: '#2F79EA',
    latitude,
    longitude,
    ownerName: venue.owner_name || venue.contact_name || venue.name || 'Venue',
    ownerEmail: venue.contact_email || '',
    accountId: venue.id || '—',
    externalUrl: '',
    statusKey: 'live',
  };
};

const resolveAssetUrl = (url, backendUrl) => {
  if (!url) return '';
  return url.startsWith('/') ? `${backendUrl}${url}` : url;
};

const getReferralStageRank = (referral) => {
  if (referral?.details?.invitation_accepted_at) return 0;
  if (referral?.details?.invite_sent_at) return 1;
  if (referral?.status === 'approved') return 2;
  return 3;
};

const buildTimelineTooltip = (label, timestamp, fallback) => {
  if (!timestamp) return fallback;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return `${label}: ${parsed.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`;
};

export const AdminApprovalsPanel = ({
  approveVenueApplication,
  listingApplications,
  mobileMode = false,
  onContactUser,
  onApproveReferral,
  onMarkReferralInviteSent,
  onRejectReferral,
  onRemoveReferral,
  onRequestMediaChanges,
  pendingApprovalCount,
  pendingVenueApps,
  revisionVenueApps,
  referredVenueItems,
  selectedVenueApp,
  setSelectedVenueApp,
  backendUrl,
  venues,
  venueAction,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMapId, setSelectedMapId] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('pending');
  const [selectedPendingCardId, setSelectedPendingCardId] = useState('');
  const [pendingSelectionNotice, setPendingSelectionNotice] = useState('');
  const [expandedHostHintCardId, setExpandedHostHintCardId] = useState('');
  const [selectedReferralCardId, setSelectedReferralCardId] = useState('');
  const [selectedReferralHostId, setSelectedReferralHostId] = useState('');
  const [referredMapMode, setReferredMapMode] = useState('all');
  const [pendingMapFilters, setPendingMapFilters] = useState(['pending']);

  const visibleVenueApps = useMemo(() => pendingVenueApps.filter((app) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return [app.restaurant_name, app.user?.email, app.city, app.region].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
  }), [pendingVenueApps, searchQuery]);

  const visibleRevisionApps = useMemo(() => (revisionVenueApps || []).filter((app) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return [app.restaurant_name, app.user?.email, app.city, app.region].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
  }), [revisionVenueApps, searchQuery]);
  const visibleReferralItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return [...(referredVenueItems || [])]
      .filter((entry) => {
        if (!query) return true;
        return [
          entry.details?.venue_name,
          entry.details?.venue_address,
          entry.details?.venue_category,
          entry.details?.referrer_name,
          entry.details?.referrer_email,
        ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
      })
      .sort((a, b) => {
        const rankDifference = getReferralStageRank(a) - getReferralStageRank(b);
        if (rankDifference !== 0) return rankDifference;
        const aTimestamp = a.details?.invitation_accepted_at || a.details?.invite_sent_at || a.approved_at || a.created_at || '';
        const bTimestamp = b.details?.invitation_accepted_at || b.details?.invite_sent_at || b.approved_at || b.created_at || '';
        return String(bTimestamp).localeCompare(String(aTimestamp));
      });
  }, [referredVenueItems, searchQuery]);

  const referredHostGroups = useMemo(() => {
    const groupedHosts = new Map();
    visibleReferralItems.forEach((referral) => {
      const hostId = referral.referrer_id || referral.referrer_user?.id;
      if (!hostId) return;

      const existing = groupedHosts.get(hostId) || {
        hostId,
        hostName: referral.details?.referrer_name || referral.referrer_user?.name || 'Host',
        hostEmail: referral.details?.referrer_email || referral.referrer_user?.email || '',
        hostListings: [],
        referredVenues: [],
        venueReferralCount: 0,
      };

      existing.venueReferralCount += 1;
      existing.hostListings = [...existing.hostListings, ...(referral.referrer_host_listings || [])];
      existing.referredVenues = [
        ...existing.referredVenues,
        {
          id: referral.id,
          venueName: referral.details?.venue_name || 'Referred venue',
          venueAddress: referral.details?.venue_address || 'Address unavailable',
        },
      ];
      groupedHosts.set(hostId, existing);
    });

    return Array.from(groupedHosts.values()).map((group) => {
      const hostListings = dedupeById(group.hostListings);
      const activeCount = hostListings.filter((listing) => listing.status === 'approved').length;
      const earlyCount = hostListings.filter((listing) => listing.status === 'rejected').length;
      const mapItems = dedupeById(hostListings.map(buildListingMapItem));

      return {
        ...group,
        activeCount,
        earlyCount,
        mapItems,
        mappedCount: mapItems.length,
      };
    });
  }, [visibleReferralItems]);

  const referredHostGroupMap = useMemo(() => new Map(referredHostGroups.map((group) => [group.hostId, group])), [referredHostGroups]);
  const selectedReferredHostGroup = selectedReferralHostId ? referredHostGroupMap.get(selectedReferralHostId) || null : null;

  const pendingVenueMapItems = useMemo(() => dedupeById(visibleVenueApps.map(buildVenueMapItem)), [visibleVenueApps]);
  const activeListingMapItems = useMemo(() => dedupeById((listingApplications || []).filter((listing) => listing.status === 'approved').map(buildListingMapItem)), [listingApplications]);
  const earlyListingMapItems = useMemo(() => dedupeById((listingApplications || []).filter((listing) => listing.status === 'rejected').map(buildListingMapItem)), [listingApplications]);
  const activeVenueMapItems = useMemo(() => dedupeById((venues || []).filter((venue) => venue.is_active !== false).map(buildActiveVenueMapItem)), [venues]);

  const mapItems = useMemo(() => {
    if (categoryFilter === 'referred') {
      if (referredMapMode === 'only-selected') {
        return selectedReferredHostGroup?.mapItems || [];
      }
      return dedupeById(referredHostGroups.flatMap((group) => group.mapItems));
    }

    const nextItems = [];
    if (pendingMapFilters.includes('pending')) nextItems.push(...pendingVenueMapItems);
    if (pendingMapFilters.includes('early-hosts')) nextItems.push(...earlyListingMapItems);
    if (pendingMapFilters.includes('listings')) nextItems.push(...activeListingMapItems);
    if (pendingMapFilters.includes('venues')) nextItems.push(...activeVenueMapItems);
    return dedupeById(nextItems);
  }, [activeListingMapItems, activeVenueMapItems, categoryFilter, earlyListingMapItems, pendingMapFilters, pendingVenueMapItems, referredHostGroups, referredMapMode, selectedReferredHostGroup]);

  useEffect(() => {
    if (categoryFilter === 'pending' && pendingSelectionNotice) {
      setSelectedMapId('');
      return;
    }
    if (!mapItems.length) {
      setSelectedMapId('');
      return;
    }
    if (!mapItems.some((item) => item.id === selectedMapId)) {
      setSelectedMapId(mapItems[0].id);
    }
  }, [categoryFilter, mapItems, pendingSelectionNotice, selectedMapId]);

  const selectedMapItemId = selectedMapId || mapItems[0]?.id || '';
  const selectedMapItem = mapItems.find((item) => item.id === selectedMapItemId) || mapItems[0] || null;

  const focusPendingMapItem = (app, showUnavailableToast = false) => {
    const mapItem = buildVenueMapItem(app);
    setSelectedPendingCardId(app.id || '');
    setCategoryFilter('pending');
    setPendingMapFilters((current) => (current.includes('pending') ? current : [...current, 'pending']));
    if (mapItem) {
      setPendingSelectionNotice('');
      setSelectedMapId(mapItem.id);
      return;
    }
    setSelectedMapId('');
    setPendingSelectionNotice(`${app.restaurant_name || 'This venue'} does not have map coordinates yet, so it can’t be focused on the map.`);
    if (showUnavailableToast) {
      toast.info('This pending venue does not have map coordinates yet.');
    }
  };

  const focusReferredHost = (referral) => {
    const hostId = referral.referrer_id || referral.referrer_user?.id;
    if (!hostId) return;
    const hostGroup = referredHostGroupMap.get(hostId);
    setSelectedReferralCardId(referral.id || '');
    setCategoryFilter('referred');
    setSelectedReferralHostId(hostId);
    setReferredMapMode('only-selected');
    setSelectedMapId(hostGroup?.mapItems?.[0]?.id || '');
  };

  const buildCoverageHint = (referral) => {
    const normalizedLocation = String(referral.details?.venue_address || '').toLowerCase();
    const activeVenues = (venues || []).filter((venue) => venue.is_active !== false);
    if (!normalizedLocation || !activeVenues.length) {
      return { title: 'Coverage hint', copy: 'No Allure venue match found yet.' };
    }

    const locationParts = normalizedLocation.split(',').map((part) => part.trim()).filter(Boolean);
    const match = activeVenues.find((venue) => {
      const address = String(venue.address || '').toLowerCase();
      return locationParts.some((part) => part.length > 2 && address.includes(part));
    });

    if (match) {
      return {
        title: 'Coverage hint',
        copy: `Closest Allure area match: ${match.name} · ${match.address}`,
      };
    }

    return { title: 'Coverage hint', copy: 'No Allure venue match found yet.' };
  };

  const buildVenueInviteMessage = (referral) => {
    const venueName = referral.details?.venue_name || 'your venue';
    const inviteUrl = buildShareUrl(`/venue-invite/${referral.id}`);
    return `Hi ${venueName},

You’ve been invited to join Allure — a platform connecting Airbnb guests with carefully selected venues.

Download the app and continue here:
${inviteUrl}`;
  };

  const pendingLayerMeta = [
    { id: 'pending', label: 'Pending', count: pendingVenueMapItems.length, tone: 'pending' },
    { id: 'early-hosts', label: 'Early', count: earlyListingMapItems.length, tone: 'early' },
    { id: 'listings', label: 'Listings', count: activeListingMapItems.length, tone: 'active' },
    { id: 'venues', label: 'Venues', count: activeVenueMapItems.length, tone: 'venue' },
  ];

  const approvalsColumnHeight = 'calc(100vh - 230px)';

  const markInviteSentAndCopy = async (referral) => {
    const copied = await copyInviteVenueText(buildVenueInviteMessage(referral));
    await onMarkReferralInviteSent(referral.id);
    toast.success(copied ? 'Venue invite copied' : 'Copy prompt opened');
  };

  const referredMapTitle = categoryFilter === 'referred'
    ? referredMapMode === 'only-selected' && selectedReferredHostGroup
      ? `${selectedReferredHostGroup.hostName}'s listings`
      : 'Referred host listings'
    : 'Approving map';

  const referredEmptyCopy = referredMapMode === 'only-selected'
    ? selectedReferredHostGroup
      ? 'This host has no mapped active or early listings yet.'
      : 'Choose a referred host card to focus the map.'
    : 'Hosts with mapped active or early listings will appear here.';

  const pendingEmptyCopy = pendingMapFilters.length
    ? pendingSelectionNotice || 'No items match the selected pending-map filters yet.'
    : 'Select at least one layer to populate the map.';

  if (selectedVenueApp) {
    return (
      <ApprovalsDetailView
        selectedVenueApp={selectedVenueApp}
        onBack={() => setSelectedVenueApp(null)}
        approveVenueApplication={approveVenueApplication}
        venueAction={venueAction}
        onContactUser={onContactUser}
        onApproveReferral={onApproveReferral}
        onMarkReferralInviteSent={onMarkReferralInviteSent}
        onRejectReferral={onRejectReferral}
        onRemoveReferral={onRemoveReferral}
        onRequestMediaChanges={onRequestMediaChanges}
        backendUrl={backendUrl}
        focusReferredHost={focusReferredHost}
        focusPendingMapItem={focusPendingMapItem}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: mobileMode ? '1fr' : 'minmax(0, 1.1fr) minmax(360px, 0.9fr)', gap: 14, alignItems: 'stretch', height: mobileMode ? 'auto' : approvalsColumnHeight, overflow: mobileMode ? 'visible' : 'hidden' }}>
      <style>{`@keyframes admin-referral-pulse { 0% { box-shadow: 0 0 0 0 rgba(23, 138, 47, 0.18), 0 10px 28px rgba(23, 138, 47, 0.08); } 70% { box-shadow: 0 0 0 10px rgba(23, 138, 47, 0), 0 10px 28px rgba(23, 138, 47, 0.16); } 100% { box-shadow: 0 0 0 0 rgba(23, 138, 47, 0), 0 10px 28px rgba(23, 138, 47, 0.08); } }`}</style>

      {/* Mobile: map shown ABOVE the cards (not hidden) */}
      {mobileMode && (
        <div data-testid="admin-approving-map-column-mobile" style={{ display: 'grid', gap: 8 }}>
          <AdminGeoMapCanvas
            autoSelectFirst={!(categoryFilter === 'pending' && pendingSelectionNotice)}
            dataTestId="admin-approving-inline-map"
            emptyCopy={categoryFilter === 'pending' ? pendingEmptyCopy : referredEmptyCopy}
            height={280}
            items={mapItems}
            onSelect={setSelectedMapId}
            selectedId={selectedMapItemId}
            sticky={false}
            title={referredMapTitle}
          />
        </div>
      )}

      <div data-testid="admin-approving-list-column" style={{ display: 'grid', gap: 14, minHeight: 0, overflowY: mobileMode ? 'visible' : 'auto', paddingRight: 6 }}>
        <Card>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700 }}>Waiting for venue approval</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>Only new venue applications appear here until you approve or decline them.</p>
            </div>
            <Badge text={`${pendingApprovalCount} pending`} color="red" />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <StatusChip tone="pending" label="Pending" count={pendingVenueApps.length} active={categoryFilter === 'pending'} onClick={() => setCategoryFilter('pending')} dataTestId="admin-approving-filter-pending" />
            <StatusChip tone="early" label="Awaiting revisions" count={(revisionVenueApps || []).length} active={categoryFilter === 'revisions'} onClick={() => setCategoryFilter('revisions')} dataTestId="admin-approving-filter-revisions" />
            <StatusChip tone="info" label="Referred" count={(referredVenueItems || []).length} active={categoryFilter === 'referred'} onClick={() => setCategoryFilter('referred')} dataTestId="admin-approving-filter-referred" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E7E7E7', borderRadius: 14, padding: '12px 14px', marginTop: 14 }}>
            <Search size={18} color="#666" />
            <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} data-testid="admin-approving-search-input" placeholder={categoryFilter === 'pending' ? 'Search by venue name, e-mail, city, or region' : 'Search by venue, host, or location'} style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 15 }} />
          </div>
        </Card>

        {categoryFilter === 'pending' ? visibleVenueApps.map((app) => (
          <Card key={app.id} onClick={() => focusPendingMapItem(app)} data-testid={`admin-approving-card-${app.id}`} style={{ border: (selectedPendingCardId || visibleVenueApps[0]?.id) === app.id ? '1px solid rgba(87, 119, 213, 0.55)' : '1px solid #E7E7E7', boxShadow: (selectedPendingCardId || visibleVenueApps[0]?.id) === app.id ? '0 10px 28px rgba(87, 119, 213, 0.12)' : undefined, position: 'relative', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 700, margin: 0 }}>{app.restaurant_name || 'Venue application'}</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>Owner: {[app.first_name, app.last_name].filter(Boolean).join(' ') || app.user?.name || '—'}</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{app.contact_email || app.user?.email || '—'}</p>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#111' }}>{[app.street, app.city, app.region, app.country].filter(Boolean).join(', ') || [app.city, app.region, app.country].filter(Boolean).join(', ') || 'Address unavailable'}</p>
                <p style={{ margin: '8px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#111' }}>Account ID: {app.user?.customer_id || '—'}</p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#111' }}>{app.venue_type || 'Venue'} · Step {app.current_step || '—'}/7</p>
                {(app.photo_urls || []).length ? (
                  <div data-testid={`admin-approving-photo-strip-${app.id}`} style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {(app.photo_urls || []).slice(0, 3).map((url, index) => (
                      <img key={`${app.id}-preview-${url}`} src={resolveAssetUrl(url, backendUrl)} alt={`Pending venue preview ${index + 1}`} style={{ width: 64, height: 64, borderRadius: 12, objectFit: 'cover', border: '1px solid #EFEFEF' }} />
                    ))}
                  </div>
                ) : null}
                {!buildVenueMapItem(app) ? <p data-testid={`admin-approving-map-unavailable-${app.id}`} style={{ margin: '8px 0 0', fontSize: 13, color: '#D9822B' }}>Map coordinates unavailable for this venue yet.</p> : null}
              </div>
              <Badge text="pending" color="blue" />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button type="button" onClick={(event) => { event.stopPropagation(); setSelectedVenueApp({ ...app, sourceType: 'pending' }); }} data-testid={`admin-approving-open-details-${app.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #111', background: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Open details</button>
              <button type="button" onClick={(event) => { event.stopPropagation(); onContactUser(app.user?.id || app.user_id); }} data-testid={`admin-approving-contact-user-${app.id}`} style={{ marginLeft: 'auto', padding: '10px 14px', borderRadius: 12, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 700 }}>Contact owner</button>
            </div>
          </Card>
        )) : categoryFilter === 'revisions' ? visibleRevisionApps.map((app) => {
          const issues = Array.isArray(app.revision_field_issues) ? app.revision_field_issues : [];
          const rejectedCount = (app.rejected_photos || []).length;
          const videoRejected = !!app.rejected_video_url;
          const sentAt = app.revision_requested_at ? new Date(app.revision_requested_at) : null;
          const daysAgo = sentAt ? Math.max(0, Math.floor((Date.now() - sentAt.getTime()) / (1000 * 60 * 60 * 24))) : null;
          return (
            <Card key={`rev-${app.id}`} data-testid={`admin-approving-revision-card-${app.id}`} style={{ border: '1px solid rgba(214, 137, 16, 0.35)', boxShadow: '0 10px 24px rgba(214, 137, 16, 0.10)', background: 'linear-gradient(180deg, rgba(255, 240, 220, 0.4), #FFFFFF)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 700, margin: 0 }}>{app.restaurant_name || 'Venue application'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>Owner: {[app.first_name, app.last_name].filter(Boolean).join(' ') || app.user?.name || '—'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{app.contact_email || app.user?.email || '—'}</p>
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: '#7A5613', fontWeight: 700 }}>
                    Revision sent{daysAgo !== null ? ` · ${daysAgo === 0 ? 'today' : `${daysAgo}d ago`}` : ''} — waiting on owner
                  </p>
                  <p data-testid={`admin-approving-revision-summary-${app.id}`} style={{ margin: '8px 0 0', fontSize: 13, color: '#111', lineHeight: 1.5 }}>
                    {[
                      rejectedCount > 0 ? `${rejectedCount} photo${rejectedCount === 1 ? '' : 's'} flagged` : null,
                      videoRejected ? 'video flagged' : null,
                      issues.length > 0 ? `${issues.length} info fix${issues.length === 1 ? '' : 'es'}` : null,
                    ].filter(Boolean).join(' · ') || 'Pending owner action'}
                  </p>
                  {issues.length > 0 ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {issues.slice(0, 5).map((issue, idx) => {
                        const labels = { contact: 'Contact', business: 'Business / VAT', address: 'Address', hours: 'Hours', social: 'Social', type: 'Type', other: 'Other' };
                        return (
                          <span key={`rev-tag-${app.id}-${idx}`} style={{ padding: '3px 8px', borderRadius: 9999, background: 'rgba(214, 137, 16, 0.14)', color: '#7A5613', fontSize: 11, fontWeight: 700 }}>
                            {labels[issue?.category] || issue?.category || 'Detail'}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
                <Badge text="awaiting revisions" color="orange" />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setSelectedVenueApp({ ...app, sourceType: 'pending' })} data-testid={`admin-approving-revision-open-${app.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #111', background: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Open details</button>
                <button type="button" onClick={() => onContactUser(app.user?.id || app.user_id)} data-testid={`admin-approving-revision-contact-${app.id}`} style={{ marginLeft: 'auto', padding: '10px 14px', borderRadius: 12, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 700 }}>Contact owner</button>
              </div>
            </Card>
          );
        }) : visibleReferralItems.map((referral, index) => (
          (() => {
            const coverageHint = buildCoverageHint(referral);
            const hostId = referral.referrer_id || referral.referrer_user?.id || '';
            const hostGroup = referredHostGroupMap.get(hostId);
            const isSelectedCard = selectedReferralCardId === referral.id;
            const isApprovedReferral = referral.status === 'approved' || Boolean(referral.details?.invitation_accepted_at) || Boolean(referral.details?.invite_sent_at);
            const invitationAccepted = Boolean(referral.details?.invitation_accepted_at);
            const inviteSentTimestamp = referral.details?.invite_sent_at || referral.details?.invitation_accepted_at || '';
            const inviteSent = Boolean(inviteSentTimestamp);
            const shouldHighlightAccepted = invitationAccepted && index < 3;
            return (
          <Card key={referral.id} onClick={() => focusReferredHost(referral)} data-testid={`admin-referred-card-${referral.id}`} style={{ border: isSelectedCard ? '1px solid rgba(23, 138, 47, 0.42)' : shouldHighlightAccepted ? '1px solid rgba(23, 138, 47, 0.28)' : '1px solid #E7E7E7', boxShadow: isSelectedCard ? '0 10px 28px rgba(23, 138, 47, 0.14)' : shouldHighlightAccepted ? '0 10px 28px rgba(23, 138, 47, 0.12)' : '0 10px 28px rgba(87, 119, 213, 0.06)', animation: shouldHighlightAccepted ? 'admin-referral-pulse 1.8s ease-in-out infinite' : undefined, position: 'relative', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
              <div>
                {shouldHighlightAccepted ? <span data-testid={`admin-referred-new-submission-${referral.id}`} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 8px', borderRadius: 9999, background: 'rgba(23, 138, 47, 0.12)', color: '#178A2F', fontSize: 11, fontWeight: 800, marginBottom: 8 }}>New submission</span> : null}
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 700, margin: 0 }}>{referral.details?.venue_name || 'Referred venue'}</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{referral.details?.venue_category || 'Venue'}</p>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#111' }}>{referral.details?.venue_address || 'Address unavailable'}</p>
                <p style={{ margin: '8px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#111' }}>Host: {referral.details?.referrer_name || referral.referrer_user?.name || 'Host'}</p>
                {hostGroup ? <p data-testid={`admin-referred-host-listing-summary-${referral.id}`} style={{ margin: '8px 0 0', fontSize: 13, color: '#111' }}>{hostGroup.activeCount} active · {hostGroup.earlyCount} early listings</p> : null}
                {hostGroup?.venueReferralCount > 1 ? (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setExpandedHostHintCardId((current) => (current === referral.id ? '' : referral.id));
                      }}
                      data-testid={`admin-referred-same-host-toggle-${referral.id}`}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: '#666', textAlign: 'left' }}
                    >
                      This host also referred {hostGroup.venueReferralCount - 1} other venue{hostGroup.venueReferralCount - 1 === 1 ? '' : 's'}. {expandedHostHintCardId === referral.id ? 'Hide' : 'Open'} list.
                    </button>
                    {expandedHostHintCardId === referral.id ? (
                      <div data-testid={`admin-referred-same-host-list-${referral.id}`} style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                        {hostGroup.referredVenues.map((venue) => (
                          <div key={`${referral.id}-${venue.id}`} style={{ borderRadius: 12, border: '1px solid #EFEFEF', padding: '10px 12px', background: '#FAFAFA' }}>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111' }}>{venue.venueName}</p>
                            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{venue.venueAddress}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div data-testid={`admin-referred-timeline-${referral.id}`} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {[
                    { key: 'approved', label: 'Approved', active: isApprovedReferral, tooltip: buildTimelineTooltip('Approved', referral.approved_at, 'Not approved yet') },
                    { key: 'invite-sent', label: 'Invite sent', active: inviteSent, tooltip: buildTimelineTooltip('Invite sent', inviteSentTimestamp, 'Invite not sent yet') },
                  ].map((step) => (
                    <span key={`${referral.id}-${step.key}`} title={step.tooltip} data-testid={`admin-referred-timeline-step-${step.key}-${referral.id}`} style={{ padding: '4px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: step.active ? (step.key === 'approved' ? 'rgba(23, 138, 47, 0.12)' : step.key === 'invite-sent' ? 'rgba(47, 121, 234, 0.12)' : 'rgba(23, 138, 47, 0.12)') : '#F3F3F3', color: step.active ? (step.key === 'invite-sent' ? '#2F79EA' : '#178A2F') : '#888', cursor: 'help' }}>{step.label}</span>
                  ))}
                </div>
                {invitationAccepted ? <span title={buildTimelineTooltip('Invitation accepted', referral.details?.invitation_accepted_at, 'Invitation not accepted yet')} data-testid={`admin-referred-invite-accepted-${referral.id}`} style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', padding: '4px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: 'rgba(23, 138, 47, 0.12)', color: '#178A2F', cursor: 'help' }}>Invitation accepted</span> : null}
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#2F79EA', lineHeight: 1.35 }}>{coverageHint.copy}</p>
              </div>
              <Badge text={isApprovedReferral ? 'approved' : 'referred'} color="green" />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button type="button" onClick={(event) => { event.stopPropagation(); setSelectedVenueApp({
                id: referral.id,
                sourceType: 'referred',
                restaurant_name: referral.details?.venue_name || '',
                venue_type: referral.details?.venue_category || '',
                venue_category: referral.details?.venue_category || '',
                venue_address: referral.details?.venue_address || '',
                venue_link: referral.details?.venue_link || '',
                reason: referral.details?.reason || '',
                referred_by_name: referral.details?.referrer_name || referral.referrer_user?.name || '',
                referred_by_email: referral.details?.referrer_email || referral.referrer_user?.email || '',
                coverageHint: coverageHint.copy,
                inviteMessage: buildVenueInviteMessage(referral),
                referrer_id: referral.referrer_id,
                primaryHostListing: (referral.referrer_host_listings || []).find((listing) => ['approved', 'rejected'].includes(listing.status) && listing.listing_latitude != null && listing.listing_longitude != null) || null,
                hostListings: referral.referrer_host_listings || [],
                status: referral.status,
                invitationAcceptedAt: referral.details?.invitation_accepted_at || '',
                user: referral.referrer_user || null,
              }); }} data-testid={`admin-referred-open-details-${referral.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #111', background: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Open details</button>
              {isApprovedReferral ? (
                <>
                  <button type="button" onClick={async (event) => {
                    event.stopPropagation();
                    await markInviteSentAndCopy(referral);
                  }} data-testid={`admin-referred-invite-venue-${referral.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: '#2F79EA', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Invite Venue</button>
                  <button type="button" onClick={(event) => { event.stopPropagation(); onRemoveReferral(referral.id); }} data-testid={`admin-referred-remove-${referral.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid rgba(221, 6, 8, 0.18)', background: '#FFFFFF', color: '#DD0608', cursor: 'pointer', fontWeight: 700 }}>Remove</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={(event) => { event.stopPropagation(); onApproveReferral(referral.id); }} data-testid={`admin-referred-approve-${referral.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: '#00C853', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Approve</button>
                  <button type="button" onClick={(event) => { event.stopPropagation(); onRejectReferral(referral.id); }} data-testid={`admin-referred-decline-${referral.id}`} style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: '#DD0608', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Decline</button>
                </>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); onContactUser(referral.referrer_user?.id || referral.referrer_id); }} data-testid={`admin-referred-contact-user-${referral.id}`} style={{ marginLeft: 'auto', padding: '10px 14px', borderRadius: 12, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 700 }}>Contact host</button>
            </div>
          </Card>
            );
          })()
        ))}

        {categoryFilter === 'pending' && !visibleVenueApps.length ? (
          <EmptyState
            icon={FileCheck}
            tone="active"
            title="Inbox zero — no pending venue applications"
            body="When a venue owner finishes their application (step 7/7) it lands here ready for your decision. You'll also see how their pin lines up against active venues and listings on the right rail."
            dataTestId="admin-approving-empty-state"
          />
        ) : null}
        {categoryFilter === 'revisions' && !visibleRevisionApps.length ? (
          <EmptyState
            icon={FileClock}
            tone="early"
            title="No venues waiting on revisions"
            body="Venues you sent back for tweaks (photos, hours, address, etc.) appear here once they re-submit. You can also message the owner directly from each card to nudge them."
            dataTestId="admin-approving-revisions-empty-state"
          />
        ) : null}
        {categoryFilter === 'referred' && !visibleReferralItems.length ? (
          <EmptyState
            icon={UserPlus}
            tone="info"
            title="No referred venues right now"
            body="When a host refers a venue (via the in-app referral flow), the referrer + referee land here so you can approve or invite them to apply formally."
            dataTestId="admin-approving-referred-empty-state"
          />
        ) : null}
      </div>

      {!mobileMode && (
      <div data-testid="admin-approving-map-column" style={{ display: 'grid', gap: 8, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
        <Card data-testid="admin-approving-map-controls-card" style={{ padding: 14 }}>
          <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 700, color: '#111' }}>
            {categoryFilter === 'pending' ? 'Map layers' : 'Referred host filters'}
          </p>

          {categoryFilter === 'pending' ? (
            <>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                {pendingLayerMeta.map((filter) => (
                  <StatusChip
                    key={filter.id}
                    tone={filter.tone}
                    label={filter.label}
                    count={filter.count}
                    active={pendingMapFilters.includes(filter.id)}
                    onClick={() => setPendingMapFilters((current) => toggleMultiFilter(current, filter.id))}
                    dataTestId={`admin-approving-pending-map-filter-${filter.id}`}
                  />
                ))}
              </div>
              <p data-testid="admin-approving-pending-map-summary" style={{ margin: '10px 0 0', fontSize: 12, color: '#666' }}>
                {mapItems.length} pin{mapItems.length === 1 ? '' : 's'} from {pendingMapFilters.length || 0} layer{pendingMapFilters.length === 1 ? '' : 's'}{selectedPendingCardId ? ' · 1 selected' : ''}
              </p>
              {pendingSelectionNotice ? <p data-testid="admin-approving-pending-map-notice" style={{ margin: '6px 0 0', fontSize: 12, color: '#B56B11', lineHeight: 1.45 }}>{pendingSelectionNotice}</p> : null}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                <button type="button" onClick={() => setReferredMapMode('all')} data-testid="admin-referred-map-filter-all" style={{ padding: '6px 12px', borderRadius: 10, border: '1px solid #E7E7E7', background: referredMapMode === 'all' ? '#111' : '#FFF', color: referredMapMode === 'all' ? '#FFF' : '#111', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>All</button>
                <button type="button" onClick={() => setReferredMapMode('only-selected')} data-testid="admin-referred-map-filter-only-selected" style={{ padding: '6px 12px', borderRadius: 10, border: '1px solid #E7E7E7', background: referredMapMode === 'only-selected' ? '#111' : '#FFF', color: referredMapMode === 'only-selected' ? '#FFF' : '#111', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Only selected</button>
              </div>
              <div data-testid="admin-referred-map-summary" style={{ marginTop: 10 }}>
                {referredMapMode === 'all' ? (
                  <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                    {referredHostGroups.length} host{referredHostGroups.length === 1 ? '' : 's'} · {referredHostGroups.reduce((total, group) => total + group.activeCount, 0)} active · {referredHostGroups.reduce((total, group) => total + group.earlyCount, 0)} early
                  </p>
                ) : selectedReferredHostGroup ? (
                  <p data-testid="admin-referred-selected-host-name" style={{ margin: 0, fontSize: 12, color: '#666' }}>
                    {selectedReferredHostGroup.hostName} · {selectedReferredHostGroup.activeCount} active · {selectedReferredHostGroup.earlyCount} early · {selectedReferredHostGroup.mappedCount} mapped
                  </p>
                ) : (
                  <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Tap a referred host card to isolate that host on the map.</p>
                )}
              </div>
            </>
          )}
        </Card>

        <AdminGeoMapCanvas
          autoSelectFirst={!(categoryFilter === 'pending' && pendingSelectionNotice)}
          dataTestId="admin-approving-inline-map"
          emptyCopy={categoryFilter === 'pending' ? pendingEmptyCopy : referredEmptyCopy}
          height={520}
          items={mapItems}
          onSelect={setSelectedMapId}
          selectedId={selectedMapItemId}
          sticky={false}
          title={referredMapTitle}
        />
      </div>
      )}
    </div>
  );
};